import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Bell, Plus, Check, Trash2, User, Users, ChevronDown, X, Sun, Search, AlertTriangle, CalendarClock, ArrowRight, UserPlus, ArrowDownUp, LayoutDashboard, ShoppingCart, List, LayoutGrid, Magnet } from "lucide-react";
import WhiteBoard from "./WhiteBoard";
import { printRunSheet } from "./runsheetPrint";
import { KIND_OPTIONS } from "./boardUtils";
import { SuggestDayPanel } from "./SuggestDayPanel";
import { AutoRemindersToggle } from "./AutoRemindersToggle";

const todayISO = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD local
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toLocaleDateString("en-CA"); };
const prettyDate = (iso) => {
  if (!iso) return "Anytime";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; };
const daysOverdue = (iso) => { const d = new Date(iso + "T00:00:00"); const t = new Date(); t.setHours(0, 0, 0, 0); return Math.max(0, Math.round((t - d) / 86400000)); };
const longToday = () => new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const BUCKETS = [
  { key: "overdue",  label: "Overdue",  icon: Bell, color: "#dc2626", soft: "bg-red-50 border-red-300 text-red-700" },
  { key: "today",    label: "Today",    icon: Sun,  color: "#ca8a04", soft: "bg-amber-50 border-amber-300 text-amber-800" },
  { key: "tomorrow", label: "Tomorrow", icon: Bell, color: "#0284c7", soft: "bg-sky-50 border-sky-300 text-sky-700" },
  { key: "future",   label: "Future",   icon: Bell, color: "#64748b", soft: "bg-slate-50 border-slate-300 text-slate-600" },
];

function bucketOf(r) {
  const t = todayISO(), tm = addDays(1);
  const d = r.due_date || null;
  if (!d) return "today";
  if (d < t) return "overdue";
  if (d === t) return "today";
  if (d === tm) return "tomorrow";
  return "future";
}

export default function Reminders({ user, onNav, onOpenClient, onNewClient, onViewChange, initialView = "list" }) {
  const [view, setViewRaw] = useState(initialView);
  const setView = (v) => { setViewRaw(v); onViewChange && onViewChange(v); };
  const [boardKey, setBoardKey] = useState(0);
  const [all, setAll] = useState([]);
  const [scope, setScope] = useState("mine"); // all | mine
  const [bucket, setBucket] = useState("today");
  const [sortDir, setSortDir] = useState("asc"); // asc = oldest→newest, desc = newest→oldest
  const [adding, setAdding] = useState(false);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [reassignId, setReassignId] = useState(null);
  const [team, setTeam] = useState([]);
  const [manualRem, setManualRem] = useState(null);

  const load = useCallback(async () => {
    try { setAll(await api("GET", "/reminders")); } catch { setAll([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api("GET", "/team").then(setTeam).catch(() => setTeam([])); }, []);

  const mine = (r) => (r.assigned_to || "").trim().toLowerCase() === (user?.name || "").trim().toLowerCase();
  const scoped = all.filter((r) =>
    scope === "all" ? true
      : scope === "mine" ? mine(r)
        : (r.assigned_to || "").trim().toLowerCase() === scope.trim().toLowerCase()
  );
  const counts = BUCKETS.reduce((acc, b) => { acc[b.key] = scoped.filter((r) => bucketOf(r) === b.key).length; return acc; }, {});
  const rows = scoped.filter((r) => bucketOf(r) === bucket)
    .sort((a, b) => {
      const cmp = (a.due_date || "9999").localeCompare(b.due_date || "9999");
      return sortDir === "asc" ? cmp : -cmp;
    });

  const [sel, setSel] = useState(new Set());
  const [bulkWho, setBulkWho] = useState("__pick");
  useEffect(() => { setSel(new Set()); }, [bucket, scope, view]);
  const toggleSel = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulk = async (action) => {
    const ids = [...sel];
    if (action === "delete" && !window.confirm(`Delete ${ids.length} reminder${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    if (action === "assign" && bulkWho === "__pick") { toast.error("Pick who to send them to"); return; }
    await api("POST", "/reminders/bulk", { ids, action, assigned_to: action === "assign" ? bulkWho : null });
    toast.success(action === "delete" ? `Deleted ${ids.length}` : action === "done" ? `Marked ${ids.length} done` : `Sent ${ids.length} to ${bulkWho || "everyone"}`);
    setSel(new Set()); load();
  };

  const goToReminder = (r) => { if (r.client_id) { onOpenClient(r.client_id); } else { setManualRem(r); } };
  const reschedule = async (r, date) => { await api("PATCH", `/reminders/${r.id}?due_date=${date}`); setRescheduleId(null); toast.success(`Moved to ${prettyDate(date)}`); load(); };
  const reassign = async (r, name) => { await api("PATCH", `/reminders/${r.id}?assigned_to=${encodeURIComponent(name)}`); setReassignId(null); toast.success(name ? `Sent to ${name}` : "Set for everyone"); load(); };
  const toggleBoard = async (r) => {
    if (!r.on_whiteboard && !r.assigned_to) { toast.error("Send it to a staff member first — each magnet sits in someone's row"); return; }
    await api("PATCH", `/reminders/${r.id}?on_board=${!r.on_whiteboard}${r.on_whiteboard ? "" : `&kind=${r.kind || "job"}`}`);
    toast.success(r.on_whiteboard ? "Taken off the White Board" : `Placed on the White Board for ${r.assigned_to}`);
    load();
  };

  const activeBucket = BUCKETS.find((b) => b.key === bucket);
  const totalOpen = scoped.length;

  return (
    <div data-testid="reminders-panel">
      {/* Morning header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900 leading-tight">{greeting()}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h1>
          <p className="text-sm text-slate-500 mt-1">{longToday()} · <span className="font-semibold text-slate-700">{totalOpen} thing{totalOpen === 1 ? "" : "s"} to do</span></p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <button onClick={() => onNav && onNav("tab:switchboard")} data-testid="reminders-dashboard-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#123a63] hover:bg-[#0d2a47] text-white text-base font-bold px-6 py-3.5 shadow-md transition-colors">
            <LayoutDashboard className="h-5 w-5" /> DASHBOARD
          </button>
          <button onClick={() => onNewClient && onNewClient()} data-testid="reminders-add-client-btn"
            className="inline-flex items-center gap-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-base font-bold px-6 py-3.5 shadow-md transition-colors">
            <UserPlus className="h-5 w-5" /> + ADD NEW CLIENT
          </button>
          <button onClick={() => onNav && onNav("tab:pos")} data-testid="reminders-pos-btn"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-base font-bold px-6 py-3.5 shadow-md transition-colors">
            <ShoppingCart className="h-5 w-5" /> POS
          </button>
          <button onClick={() => setAdding(true)} data-testid="add-reminder-btn"
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-base font-bold px-6 py-3.5 shadow-md transition-colors">
            <Plus className="h-5 w-5" /> ADD A NEW REMINDER
          </button>
        </div>
      </div>

      {/* List / White Board toggle */}
      <div className="inline-flex items-center rounded-full border-2 border-slate-200 p-1 mb-4 mr-3 bg-white gap-1" data-testid="reminders-view-toggle">
        <button onClick={() => setView("list")} data-testid="reminders-view-list" className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${view === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}><List className="inline h-4 w-4 -mt-0.5 mr-1" />List</button>
        <button onClick={() => setView("board")} data-testid="reminders-view-board" className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${view === "board" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}><LayoutGrid className="inline h-4 w-4 -mt-0.5 mr-1" />White Board</button>
      </div>
      <AutoRemindersToggle isAdmin={user?.role === "store_admin"} onChanged={() => { load(); setBoardKey((k) => k + 1); }} />

      {view === "board" ? (
        <WhiteBoard reloadKey={boardKey} onOpenClient={onOpenClient} onAddAway={() => setAdding("away")}
          onPrintRun={(who) => { const d = new Date(); if (d.getDay() === 0) d.setDate(d.getDate() + 1); printRunSheet(who, d.toLocaleDateString("en-CA")); }} />
      ) : (<>
      {/* Overdue warning — impossible to miss */}
      {counts.overdue > 0 && bucket !== "overdue" && (
        <button onClick={() => setBucket("overdue")} data-testid="reminders-overdue-banner"
          className="w-full text-left rounded-2xl border-2 border-red-300 bg-red-50 p-4 mb-4 flex items-center gap-3 hover:bg-red-100 transition-colors">
          <span className="h-11 w-11 rounded-xl bg-red-600 text-white flex items-center justify-center shrink-0 animate-pulse"><AlertTriangle className="h-6 w-6" /></span>
          <span className="min-w-0 flex-1">
            <span className="block font-display font-black text-red-800 text-lg leading-tight">{counts.overdue} overdue reminder{counts.overdue === 1 ? "" : "s"} need attention</span>
            <span className="block text-[13px] text-red-700">Do them, or reschedule them — don't let them pile up.</span>
          </span>
          <span className="text-sm font-bold text-red-800 inline-flex items-center gap-1 shrink-0">Clear them <ArrowRight className="h-4 w-4" /></span>
        </button>
      )}

      {/* My / staff-picker toggle */}
      <div className="inline-flex items-center rounded-full border-2 border-slate-200 p-1 mb-4 bg-white gap-1">
        <button onClick={() => setScope("mine")} data-testid="reminders-scope-mine"
          className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${scope === "mine" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}>
          <User className="inline h-4 w-4 -mt-0.5 mr-1" />Just mine
        </button>
        <div className={`relative rounded-full ${scope !== "mine" ? "bg-slate-900" : ""}`}>
          <Users className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${scope !== "mine" ? "text-white" : "text-slate-500"}`} />
          <select
            value={scope === "mine" ? "all" : scope}
            onChange={(e) => setScope(e.target.value)}
            data-testid="reminders-scope-staff"
            className={`appearance-none bg-transparent pl-9 pr-8 py-2 rounded-full text-sm font-bold cursor-pointer focus:outline-none ${scope !== "mine" ? "text-white" : "text-slate-500 hover:text-slate-800"}`}>
            <option value="all">Everyone's</option>
            {team.map((m) => (
              <option key={m.id || m.name} value={m.name} className="text-slate-900">{m.name}</option>
            ))}
          </select>
          <ChevronDown className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 ${scope !== "mine" ? "text-white" : "text-slate-500"}`} />
        </div>
      </div>

      {/* Big day buttons — colour = meaning */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {BUCKETS.map((b) => {
          const on = b.key === bucket;
          return (
            <button key={b.key} onClick={() => setBucket(b.key)} data-testid={`reminders-bucket-${b.key}`}
              className={`flex items-center justify-between rounded-2xl p-5 border-2 transition-all ${on ? "text-white border-transparent shadow-lg" : "bg-white border-slate-200 hover:border-slate-400"}`}
              style={on ? { backgroundColor: b.color } : {}}>
              <span className="text-left">
                <span className="block font-display font-black text-xl leading-tight">{b.label}</span>
                <span className={`block text-[13px] font-semibold ${on ? "text-white/80" : "text-slate-400"}`}>{counts[b.key]} reminder{counts[b.key] === 1 ? "" : "s"}</span>
              </span>
              <span className={`h-11 w-11 rounded-xl flex items-center justify-center font-black text-lg ${on ? "bg-white/20" : b.soft}`}>{counts[b.key]}</span>
            </button>
          );
        })}
      </div>

      {/* Sticky-note list */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-bold text-slate-700">{activeBucket.label} reminders</h2>
          {rows.length > 0 && (
            <label className="inline-flex items-center gap-1.5 text-[13px] font-bold text-slate-600 cursor-pointer">
              <input type="checkbox" data-testid="reminders-select-all" className="h-4 w-4"
                checked={sel.size > 0 && rows.every((r) => sel.has(r.id))}
                onChange={(e) => setSel(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} />
              Select all ({rows.length})
            </label>
          )}
        </div>
        <button
          onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
          data-testid="reminders-sort-toggle"
          title="Toggle date sort order"
          className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-200 bg-white px-3.5 py-1.5 text-[13px] font-bold text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors">
          <ArrowDownUp className="h-4 w-4" />
          {sortDir === "asc" ? "Oldest first" : "Newest first"}
        </button>
      </div>
      {sel.size > 0 && (
        <div className="sticky top-0 z-20 mb-3 flex flex-wrap items-center gap-2 rounded-2xl bg-slate-900 text-white px-4 py-2.5 shadow-lg" data-testid="reminders-bulk-bar">
          <span className="font-bold text-sm" data-testid="reminders-bulk-count">{sel.size} selected</span>
          <select value={bulkWho} onChange={(e) => setBulkWho(e.target.value)} data-testid="reminders-bulk-assignee" className="rounded-full bg-white text-slate-900 text-sm font-semibold px-3 py-1.5">
            <option value="__pick">Send to…</option>
            <option value="">Everyone</option>
            {team.map((m) => <option key={m.id || m.name} value={m.name}>{m.name}</option>)}
          </select>
          <button onClick={() => bulk("assign")} data-testid="reminders-bulk-assign-btn" className="rounded-full bg-sky-500 hover:bg-sky-600 text-sm font-bold px-4 py-1.5">Reassign</button>
          {rows.filter((r) => sel.has(r.id)).every((r) => !r.client_id) && <button onClick={() => bulk("done")} data-testid="reminders-bulk-done-btn" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-sm font-bold px-4 py-1.5">Mark done</button>}
          <button onClick={() => bulk("delete")} data-testid="reminders-bulk-delete-btn" className="rounded-full bg-red-600 hover:bg-red-700 text-sm font-bold px-4 py-1.5 inline-flex items-center gap-1"><Trash2 className="h-4 w-4" />Delete</button>
          <button onClick={() => setSel(new Set())} data-testid="reminders-bulk-clear-btn" className="ml-auto text-sm font-bold text-white/70 hover:text-white">Clear</button>
        </div>
      )}
      {rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center" data-testid="reminders-empty">
          <Check className="h-10 w-10 mx-auto text-emerald-400 mb-2" />
          {bucket === "today" && counts.overdue > 0 ? (
            <>
              <p className="font-display font-black text-slate-800 text-lg">Today's all done — great work.</p>
              <p className="text-sm text-slate-500 mt-1 mb-4">Your next job is the overdue pile.</p>
              <button onClick={() => setBucket("overdue")} data-testid="reminders-attack-overdue-btn"
                className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white text-base font-bold px-6 py-3.5 shadow-md transition-colors">
                <AlertTriangle className="h-5 w-5" /> Now attack your Overdue ({counts.overdue}) <ArrowRight className="h-5 w-5" />
              </button>
            </>
          ) : bucket === "today" && counts.overdue === 0 ? (
            <p className="font-display font-black text-slate-800 text-lg">You're completely clear — nothing today, nothing overdue. 🎉</p>
          ) : (
            <p className="font-display font-bold text-slate-700">Nothing {bucket === "overdue" ? "overdue" : `for ${activeBucket.label.toLowerCase()}`} — you're all clear.</p>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((r) => (
            <div key={r.id} data-testid="reminder-card"
              className="relative rounded-xl p-4 shadow-sm border-b-4 flex flex-col"
              style={{ background: sel.has(r.id) ? "#e0f2fe" : "#fffdf0", borderColor: BUCKETS.find((b) => b.key === bucketOf(r)).color }}>
              <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggleSel(r.id)} data-testid="reminder-select-checkbox" className="absolute top-3 right-3 h-4 w-4 cursor-pointer" />
              <p className="text-[15px] font-semibold text-slate-900 leading-snug pr-6">{r.text}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-500">
                <span className="font-bold" style={{ color: BUCKETS.find((b) => b.key === bucketOf(r)).color }}>{prettyDate(r.due_date)}</span>
                {bucketOf(r) === "overdue" && r.due_date && (
                  <span data-testid="reminder-days-overdue" className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">{daysOverdue(r.due_date)} day{daysOverdue(r.due_date) === 1 ? "" : "s"} overdue</span>
                )}
                {r.assigned_to ? <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{r.assigned_to}</span> : <span className="inline-flex items-center gap-1" data-testid="reminder-everyone-label"><Users className="h-3 w-3" />Everyone</span>}
                {r.type === "service" && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">Service</span>}
                {r.on_whiteboard && <span data-testid="reminder-on-board-badge" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500 text-white font-bold"><Magnet className="h-3 w-3" />On White Board</span>}
              </div>
              {r.client_name && (
                <button onClick={() => r.client_id && onOpenClient(r.client_id)} data-testid="reminder-client-link"
                  className="mt-2 text-left text-[13px] font-bold text-sky-700 hover:underline truncate">→ {r.client_name}</button>
              )}
              {rescheduleId === r.id ? (
                <div className="mt-3 rounded-lg border border-slate-200 bg-white p-2" data-testid="reminder-reschedule-row">
                  <p className="text-[11px] font-bold text-slate-500 mb-1.5">Move to…</p>
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    <button onClick={() => reschedule(r, todayISO())} className="px-2.5 py-1 rounded-full text-[12px] font-bold border border-amber-300 text-amber-700 hover:bg-amber-50">Today</button>
                    <button onClick={() => reschedule(r, addDays(1))} className="px-2.5 py-1 rounded-full text-[12px] font-bold border border-sky-300 text-sky-700 hover:bg-sky-50">Tomorrow</button>
                    <button onClick={() => reschedule(r, addDays(7))} className="px-2.5 py-1 rounded-full text-[12px] font-bold border border-slate-300 text-slate-600 hover:bg-slate-50">+1 week</button>
                  </div>
                  <input type="date" defaultValue={r.due_date || todayISO()} data-testid="reminder-reschedule-date"
                    onChange={(e) => e.target.value && reschedule(r, e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-sky-400" />
                </div>
              ) : reassignId === r.id ? (
                <div className="mt-3 rounded-lg border border-slate-200 bg-white p-2" data-testid="reminder-reassign-row">
                  <p className="text-[11px] font-bold text-slate-500 mb-1.5">Send this to…</p>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => reassign(r, "")} className="px-2.5 py-1 rounded-full text-[12px] font-bold border border-slate-300 text-slate-600 hover:bg-slate-50">Everyone</button>
                    {team.map((m) => (
                      <button key={m.id} onClick={() => reassign(r, m.name)} data-testid={`reminder-reassign-to-${m.id}`}
                        className={`px-2.5 py-1 rounded-full text-[12px] font-bold border ${(r.assigned_to || "").toLowerCase() === m.name.toLowerCase() ? "bg-slate-900 text-white border-slate-900" : "border-sky-300 text-sky-700 hover:bg-sky-50"}`}>
                        {m.name}{m.name === user?.name ? " (me)" : ""}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-auto pt-3 flex gap-2">
                  <button onClick={() => goToReminder(r)} data-testid="reminder-goto-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2.5 transition-colors">
                    {r.client_id ? <ArrowRight className="h-4 w-4" /> : <Bell className="h-4 w-4" />} Go to Reminder
                  </button>
                  <button onClick={() => { setReassignId(null); setRescheduleId(r.id); }} data-testid="reminder-reschedule-btn" title="Reschedule"
                    className="inline-flex items-center justify-center rounded-full border-2 border-slate-200 text-slate-600 hover:border-slate-400 text-sm font-bold px-3 py-2.5 transition-colors">
                    <CalendarClock className="h-4 w-4" />
                  </button>
                  <button onClick={() => { setRescheduleId(null); setReassignId(r.id); }} data-testid="reminder-reassign-btn" title="Send to a staff member"
                    className="inline-flex items-center justify-center rounded-full border-2 border-slate-200 text-slate-600 hover:border-slate-400 text-sm font-bold px-3 py-2.5 transition-colors">
                    <UserPlus className="h-4 w-4" />
                  </button>
                  <button onClick={() => toggleBoard(r)} data-testid="reminder-board-toggle-btn" title={r.on_whiteboard ? "On the White Board — click to take it off" : "Place on the White Board (shows in the tech's Pool-Tech run)"}
                    className={`inline-flex items-center justify-center rounded-full border-2 text-sm font-bold px-3 py-2.5 transition-colors ${r.on_whiteboard ? "border-amber-500 bg-amber-500 text-white hover:bg-amber-600" : "border-slate-200 text-slate-400 hover:border-amber-400 hover:text-amber-600"}`}>
                    <Magnet className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </>)}

      {adding && <AddReminder user={user} team={team} away={adding === "away"} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); setBoardKey((k) => k + 1); }} defaultDue={bucket === "tomorrow" ? addDays(1) : todayISO()} />}
      {manualRem && <ManualReminder rem={manualRem} team={team} user={user} onClose={() => setManualRem(null)} onChanged={() => { setManualRem(null); load(); }} />}
    </div>
  );
}

function AddReminder({ user, team, onClose, onSaved, defaultDue, away }) {
  const [text, setText] = useState("");
  const [onBoard, setOnBoard] = useState(!!away);
  const [kind, setKind] = useState(away ? "away" : "");
  const [due, setDue] = useState(defaultDue);
  const [assignee, setAssignee] = useState(""); // "" = everyone, else staff name
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [client, setClient] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!q.trim() || client) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { setResults((await api("GET", `/clients?q=${encodeURIComponent(q)}&limit=8`)) || []); } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, client]);

  const save = async (ov = {}) => {
    if (!text.trim()) { toast.error("Type what to remember"); return; }
    const o = { due, assignee, onBoard, ...ov };
    setSaving(true);
    try {
      await api("POST", "/reminders", {
        text: text.trim(), due_date: o.due || null,
        client_id: client?.id || null,
        assigned_to: o.assignee || null,
        on_board: o.onBoard ? true : null,
        kind: o.onBoard && kind ? kind : null,
      });
      toast.success("Reminder added");
      onSaved();
    } catch (e) { toast.error(e.message || "Could not save"); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="add-reminder-modal">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl">
        <div className="flex justify-between items-center p-4 border-b border-slate-100">
          <span className="font-display font-bold text-slate-900 flex items-center gap-2"><Bell className="h-5 w-5 text-amber-500" /> {away ? "Away / Out magnet" : "New reminder"}</span>
          <button onClick={onClose} className="rounded-full border border-slate-200 p-1.5 hover:bg-slate-50"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">What do you need to remember?</label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="reminder-text-input" rows={3} autoFocus
              placeholder={away ? "e.g. Off at 3pm — doctor appointment" : "e.g. Ring Mr Smith about his pump warranty"} className="w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-base focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">When?</label>
            <div className="flex gap-2 mb-2">
              <button onClick={() => setDue(todayISO())} className={`px-3 py-1.5 rounded-full text-sm font-bold border ${due === todayISO() ? "bg-amber-500 text-white border-amber-500" : "border-slate-200 text-slate-600"}`}>Today</button>
              <button onClick={() => setDue(addDays(1))} className={`px-3 py-1.5 rounded-full text-sm font-bold border ${due === addDays(1) ? "bg-sky-500 text-white border-sky-500" : "border-slate-200 text-slate-600"}`}>Tomorrow</button>
            </div>
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="reminder-date-input" className="w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-base outline-none focus:border-amber-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Who's it for?</label>
            <div className="flex flex-wrap gap-1.5" data-testid="reminder-assignee-picker">
              <button onClick={() => setAssignee("")} data-testid="reminder-assign-all"
                className={`px-3 py-1.5 rounded-full text-sm font-bold border ${assignee === "" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>Everyone</button>
              {(team || []).map((m) => (
                <button key={m.id} onClick={() => setAssignee(m.name)} data-testid={m.name === user?.name ? "reminder-assign-me" : `reminder-assign-${m.id}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-bold border ${assignee.toLowerCase() === m.name.toLowerCase() ? "bg-slate-900 text-white border-slate-900" : "border-sky-300 text-sky-700 hover:bg-sky-50"}`}>
                  {m.name}{m.name === user?.name ? " (me)" : ""}
                </button>
              ))}
            </div>
          </div>
          <BoardFields onBoard={onBoard} setOnBoard={setOnBoard} kind={kind} setKind={setKind} prefix="reminder" />
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Link a client <span className="font-normal text-slate-400">(optional — leave blank for a personal note like "my vehicle needs a service")</span></label>
            {client ? (
              <div className="flex items-center justify-between rounded-xl border-2 border-sky-200 bg-sky-50 px-3 py-2.5">
                <span className="font-semibold text-sky-800 truncate">{client.name && client.name !== "?" ? client.name : (client.company || client.phone || `Legacy #${client.legacy_id || ""}`)}</span>
                <button onClick={() => { setClient(null); setQ(""); }} className="text-sky-600 hover:text-sky-900"><X className="h-4 w-4" /></button>
              </div>
            ) : null}
            {client && !away && (
              <div className="mt-2"><SuggestDayPanel clientId={client.id} onBook={(tech, date) => {
                setAssignee(tech); setDue(date); setOnBoard(true);
                if (text.trim()) save({ assignee: tech, due: date, onBoard: true });
                else toast.info("Type what the job is, then press Save");
              }} /></div>
            )}
            {client ? null : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="reminder-client-search" placeholder="Search client name, phone…" className="w-full rounded-xl border-2 border-slate-200 pl-9 pr-3 py-2.5 text-sm outline-none focus:border-sky-400" />
                {results.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
                    {results.map((c) => (
                      <button key={c.id} data-testid={`reminder-client-result-${c.id}`} onClick={() => { setClient(c); setResults([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50 truncate">
                        {c.name && c.name !== "?" ? c.name : (c.company || c.phone || `Legacy #${c.legacy_id || ""}`)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="p-4 border-t border-slate-100 flex gap-2">
          <button onClick={onClose} className="flex-1 rounded-full border-2 border-slate-200 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={() => save()} disabled={saving} data-testid="reminder-save-btn" className="flex-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white py-2.5 text-sm font-bold disabled:opacity-50">{saving ? "Saving…" : "Save reminder"}</button>
        </div>
      </div>
    </div>
  );
}

function BoardFields({ onBoard, setOnBoard, kind, setKind, prefix }) {
  return (
    <div className="rounded-xl border-2 border-amber-200 bg-amber-50/60 p-3">
      <label className="flex items-center gap-2 text-sm font-bold text-slate-800 cursor-pointer">
        <input type="checkbox" checked={onBoard} onChange={(e) => setOnBoard(e.target.checked)} data-testid={`${prefix}-on-board-checkbox`} className="h-4 w-4" />
        <Magnet className="h-4 w-4 text-amber-600" /> Place On Board (create a magnet)
      </label>
      {onBoard && (
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid={`${prefix}-kind-select`} className="mt-2 w-full rounded-lg border-2 border-slate-200 px-2 py-1.5 text-sm bg-white">
          {KIND_OPTIONS.filter(([v]) => v !== "office").map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      )}
    </div>
  );
}

function ManualReminder({ rem, team, user, onClose, onChanged }) {
  const [text, setText] = useState(rem.text || "");
  const [onBoard, setOnBoard] = useState(!!rem.on_whiteboard);
  const [kind, setKind] = useState(rem.kind || "");
  const [due, setDue] = useState((rem.due_date || "").slice(0, 10));
  const [assignee, setAssignee] = useState(rem.assigned_to || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!text.trim()) { toast.error("Type what to remember"); return; }
    setBusy(true);
    try {
      const qs = `text=${encodeURIComponent(text.trim())}&due_date=${encodeURIComponent(due || "")}&assigned_to=${encodeURIComponent(assignee || "")}${onBoard ? "&on_board=true" : rem.on_whiteboard ? "&on_board=false" : ""}${onBoard ? `&kind=${kind}` : ""}`;
      await api("PATCH", `/reminders/${rem.id}?${qs}`);
      toast.success("Reminder updated");
      onChanged();
    } catch (e) { toast.error(e.message || "Could not save"); setBusy(false); }
  };
  const markDone = async () => {
    setBusy(true);
    try { await api("PATCH", `/reminders/${rem.id}?done=true`); toast.success("Done — nice work"); onChanged(); }
    catch (e) { toast.error(e.message); setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this reminder?")) return;
    setBusy(true);
    try { await api("DELETE", `/reminders/${rem.id}`); toast.success("Reminder deleted"); onChanged(); }
    catch (e) { toast.error(e.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="manual-reminder-modal">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl">
        <div className="flex justify-between items-center p-4 border-b border-slate-100">
          <span className="font-display font-bold text-slate-900 flex items-center gap-2"><Bell className="h-5 w-5 text-amber-500" /> Manual reminder</span>
          <button onClick={onClose} data-testid="manual-reminder-close" className="rounded-full border border-slate-200 p-1.5 hover:bg-slate-50"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Reminder</label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="manual-reminder-text" rows={3} autoFocus
              className="w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-base focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">When?</label>
            <div className="flex gap-2 mb-2">
              <button onClick={() => setDue(todayISO())} className={`px-3 py-1.5 rounded-full text-sm font-bold border ${due === todayISO() ? "bg-amber-500 text-white border-amber-500" : "border-slate-200 text-slate-600"}`}>Today</button>
              <button onClick={() => setDue(addDays(1))} className={`px-3 py-1.5 rounded-full text-sm font-bold border ${due === addDays(1) ? "bg-sky-500 text-white border-sky-500" : "border-slate-200 text-slate-600"}`}>Tomorrow</button>
            </div>
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="manual-reminder-date" className="w-full rounded-xl border-2 border-slate-200 px-3 py-2.5 text-base outline-none focus:border-amber-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Who's it for?</label>
            <div className="flex flex-wrap gap-1.5" data-testid="manual-reminder-assignee-picker">
              <button onClick={() => setAssignee("")} data-testid="manual-reminder-assign-all"
                className={`px-3 py-1.5 rounded-full text-sm font-bold border ${assignee === "" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>Everyone</button>
              {(team || []).map((m) => (
                <button key={m.id} onClick={() => setAssignee(m.name)} data-testid={m.name === user?.name ? "manual-reminder-assign-me" : `manual-reminder-assign-${m.id}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-bold border ${assignee.toLowerCase() === m.name.toLowerCase() ? "bg-slate-900 text-white border-slate-900" : "border-sky-300 text-sky-700 hover:bg-sky-50"}`}>
                  {m.name}{m.name === user?.name ? " (me)" : ""}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-5 pb-4"><BoardFields onBoard={onBoard} setOnBoard={setOnBoard} kind={kind} setKind={setKind} prefix="manual-reminder" /></div>
        <div className="p-4 border-t border-slate-100 flex gap-2">
          <button onClick={del} disabled={busy} data-testid="manual-reminder-delete" title="Delete reminder"
            className="inline-flex items-center justify-center rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50 py-2.5 px-3 text-sm font-bold disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
          <button onClick={save} disabled={busy} data-testid="manual-reminder-save"
            className="flex-1 rounded-full border-2 border-slate-200 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">{busy ? "Saving…" : "Save changes"}</button>
          <button onClick={markDone} disabled={busy} data-testid="manual-reminder-done"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 text-sm font-bold disabled:opacity-50"><Check className="h-4 w-4" /> Mark Done</button>
        </div>
      </div>
    </div>
  );
}
